/**
 * CP-6074 temporary diagnostics for intermittently empty or mismatched Portfolio Detail tables.
 * Remove once CP-6075 has shipped and the reports have stopped.
 */

/**
 * Where each response returned by `Collection.request` came from: 'network', 'in_flight' (awaited an
 * identical running request) or 'cache'.
 */
export const responseSources = new WeakMap();

/**
 * @param {*} response
 * @param {string} source
 */
export function recordResponseSource(response, source) {
    // WeakMap keys must be objects
    if (response instanceof Object) {
        responseSources.set(response, source);
    }
}

/**
 * The investor a request is made for, merging filters the way `Collection.request` does.
 *
 * @param {Object} presetFilters
 * @param {Object} filters
 * @returns {string|null}
 */
export function requestInvestorId(presetFilters, filters) {
    const value = { ...presetFilters, ...filters }.investor_id;

    return value ? String(value).replace(/^[a-z_]+:/, '') : null;
}

/**
 * @param {Array<Object>} log
 * @returns {Array<Object>} The entries after the latest `select`, or the whole log if it has none
 */
function sinceLatestSelect(log) {
    return log.slice(log.map(entry => entry.event).lastIndexOf('select') + 1);
}

/**
 * The response applied most recently since the selected investor last changed.
 *
 * @param {Array<Object>} log
 * @returns {Object|undefined}
 */
export function lastApplied(log) {
    return [...sinceLatestSelect(log)].reverse().find(entry => entry.event === 'applied');
}

/**
 * @param {Object} options
 * @param {Array<Object>} options.log - Entries of `{ event, investor_id, count }`, oldest first
 * @param {string} options.selectedInvestorId
 * @param {number} options.renderedRowCount
 * @returns {Array<string>} The conditions that hold
 */
export function getReportConditions({ log, selectedInvestorId, renderedRowCount }) {
    const sinceSelect = sinceLatestSelect(log);
    const applied = lastApplied(log);
    const lastFinish = [...sinceSelect]
        .reverse()
        .find(entry => entry.event === 'finish' && entry.investor_id === selectedInvestorId);
    const conditions = [];

    if (
        renderedRowCount === 0 &&
        !sinceSelect.some(entry => entry.event === 'start' && entry.investor_id === selectedInvestorId)
    ) {
        conditions.push('no_request');
    }

    if (applied && applied.investor_id !== selectedInvestorId) {
        conditions.push('investor_mismatch');
    }

    if (applied && applied.count > 0 && renderedRowCount === 0) {
        conditions.push('rows_not_rendered');
    }

    if (
        renderedRowCount === 0 &&
        lastFinish &&
        lastFinish.count === null &&
        !log.slice(log.indexOf(lastFinish) + 1).some(entry => entry.event === 'applied')
    ) {
        conditions.push('null_response');
    }

    return conditions;
}
